import { useState, useEffect, useCallback } from "react";
import {
  FONT_INJECT, THEME_TOKENS, LENSES, LensTab, KpiStrip, AiBriefing,
  NewsFeed, CalendarView, KanbanBoard, GoalsTracker, ListsView, QUICK_CAPTURE_LIST_ID,
  QuickCapture, GreetingStrip, DinnerWidget, DinnerView, IntentionWidget, IntentionView,
  useIsMobile, loadLocal, saveLocal,
  loadTheme, applyTheme, ThemeToggle, tint,
} from "./components";
import { initGoogleCalendar, signIn, signOut, fetchCalendarEvents } from "./googleCalendar";

const SYNC_BASE = "https://anthropic-proxy-worker.seancprince.workers.dev/sync";

// The Coach's JSON output isn't trustworthy input — guard against anything
// other than the four real recurrence values before it lands on a task.
const RECURRENCE_VALUES = ["daily", "weekly", "monthly", "annual"];
function coachRecurrence(r) { return RECURRENCE_VALUES.includes(r) ? r : null; }
function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

async function fetchSynced(key, fallback) {
  try {
    const res = await fetch(`${SYNC_BASE}/${key}`);
    if (!res.ok) return fallback;
    const data = await res.json();
    return data == null ? fallback : data;
  } catch {
    // Worker unreachable (offline, deploy in progress, etc) — fall back to
    // whatever's cached locally rather than blanking the screen.
    return fallback;
  }
}

function pushSynced(key, value) {
  fetch(`${SYNC_BASE}/${key}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(value),
  }).catch(() => {
    // Best-effort. The local copy (state + localStorage) is already
    // updated, so a failed push just means this device's edit won't be
    // visible elsewhere until the next successful push.
  });
}

const DEFAULT_TASKS = [
  { id: 1, title: "Draft ASC 606 memo", col: "Backlog", lens: "work", priority: "High", dueDate: null, recurrence: null, goalId: null, phaseId: null },
  { id: 2, title: "Consultation memo review", col: "In Progress", lens: "work", priority: "High", dueDate: null, recurrence: null, goalId: null, phaseId: null },
  { id: 3, title: "Prep webcast slides", col: "Backlog", lens: "work", priority: "Normal", dueDate: null, recurrence: null, goalId: null, phaseId: null },
  { id: 4, title: "GMU lesson plan — week 6", col: "Backlog", lens: "gmu", priority: "Normal", dueDate: null, recurrence: null, goalId: null, phaseId: null },
  { id: 5, title: "ASC 606 webcast prep", col: "In Progress", lens: "gmu", priority: "Normal", dueDate: null, recurrence: null, goalId: null, phaseId: null },
  { id: 6, title: "Schedule dentist — Liam", col: "Backlog", lens: "home", priority: "Normal", dueDate: null, recurrence: null, goalId: null, phaseId: null },
  { id: 7, title: "Summer schedule coordination", col: "In Progress", lens: "home", priority: "High", dueDate: null, recurrence: null, goalId: null, phaseId: null },
  { id: 8, title: "Weekly workout plan", col: "Backlog", lens: "growth", priority: "Normal", dueDate: null, recurrence: null, goalId: null, phaseId: null },
];

// progressMode: "manual" (bump a number yourself, e.g. "12 books") or
// "tasks" (progress derives from linked tasks getting marked Done, e.g. a
// project-shaped goal). Existing goals default to "manual" for backward
// compatibility — nothing about today's behavior changes for them.
// phases: optional milestone breakdown, empty array if the Coach hasn't
// broken this goal down yet.
const DEFAULT_GOALS = [
  { id: 1, title: "Complete ASC 606 webcast series", target: 5, progress: 2, unit: "sessions", dueDate: "2026-09-01", lens: "work", complete: false, progressMode: "manual", phases: [] },
  { id: 2, title: "Read books this year", target: 12, progress: 4, unit: "books", dueDate: "2026-12-31", lens: "growth", complete: false, progressMode: "manual", phases: [] },
  { id: 3, title: "GMU sessions prepared ahead", target: 10, progress: 3, unit: "sessions", dueDate: "2026-12-15", lens: "gmu", complete: false, progressMode: "manual", phases: [] },
  { id: 4, title: "Family date nights this quarter", target: 8, progress: 3, unit: "nights", dueDate: "2026-09-30", lens: "home", complete: false, progressMode: "manual", phases: [] },
];

export default function App() {
  const isMobile = useIsMobile();
  const [lens, setLens] = useState("all");
  const [calendarReady, setCalendarReady] = useState(false);
  const [events, setEvents] = useState(() => loadLocal("calendar-events", []));
  const [calLoading, setCalLoading] = useState(false);

  // Tasks and goals lifted to App level so calendar always has live data
  const [tasks, setTasks] = useState(() => loadLocal("kanban-Tasks", DEFAULT_TASKS));
  const [goals, setGoals] = useState(() => loadLocal("goals", DEFAULT_GOALS));
  // Shared freeform lists (Xmas ideas, vacation ideas, etc.) — same
  // lift-to-App-level + sync treatment as tasks/goals. Quick Capture is now
  // just a permanent, protected list living inside this array (see the
  // migration in the sync effect below) rather than its own synced array.
  const [lists, setLists] = useState(() => loadLocal("lists", []));
  // Daily Intention: one record per calendar date (phrases + quote + evening
  // scores), a permanent liked-quote library, and steering feedback for the
  // quote curator. Same lift-to-App-level + sync treatment as tasks/goals/lists.
  const [intentions, setIntentions] = useState(() => loadLocal("intentions", []));
  const [quoteLibrary, setQuoteLibrary] = useState(() => loadLocal("quote-library", []));
  const [quoteFeedback, setQuoteFeedback] = useState(() => loadLocal("quote-feedback", { liked: [], disliked: [] }));
  // Flips true once the initial tasks/goals sync fetch below has resolved —
  // gates the AI Briefing's auto-run so it can't fire against stale data.
  const [syncReady, setSyncReady] = useState(false);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeView, setActiveView] = useState("dashboard");
  const [theme, setTheme] = useState(loadTheme);

  const lMeta = LENSES.find(l => l.id === lens);
  const accent = lMeta?.accent ?? "var(--blue)";

  useEffect(() => { applyTheme(theme); }, [theme]);

  useEffect(() => { initGoogleCalendar(() => setCalendarReady(true)); }, []);

  // Background auto-refresh, every 5 minutes, once Google's ready. Uses the
  // silent token path (see signIn's `silent` option in googleCalendar.js) —
  // if the cached token's still valid this is instant and free; if it's
  // expired, it tries a quiet renewal with no popup, and just skips this
  // cycle on failure rather than ever interrupting with a consent prompt.
  // No calLoading toggle here on purpose — this should be invisible when it
  // works and invisible when it doesn't, only the manual button shows a spinner.
  useEffect(() => {
    if (!calendarReady) return;
    const poll = setInterval(() => {
      signIn(
        async () => {
          const ev = await fetchCalendarEvents();
          setEvents(ev);
          saveLocal("calendar-events", ev);
        },
        { silent: true, onError: () => { /* quietly skip this cycle; last-known events stay on screen */ } }
      );
    }, 5 * 60 * 1000);
    return () => clearInterval(poll);
  }, [calendarReady]);

  // On mount, pull the shared copy from the Worker's KV store. This is what
  // makes tasks/goals cross-device: localStorage alone is scoped to one
  // browser on one machine, so a second laptop would otherwise only ever
  // see DEFAULT_TASKS / DEFAULT_GOALS. If the Worker's unreachable, the
  // local cache set in useState above just stays as-is.
  useEffect(() => {
    (async () => {
      const syncedTasks = await fetchSynced("tasks", null);
      if (syncedTasks) { setTasks(syncedTasks); saveLocal("kanban-Tasks", syncedTasks); }

      const syncedGoals = await fetchSynced("goals", null);
      if (syncedGoals) { setGoals(syncedGoals); saveLocal("goals", syncedGoals); }

      const syncedIntentions = await fetchSynced("intentions", null);
      if (syncedIntentions) { setIntentions(syncedIntentions); saveLocal("intentions", syncedIntentions); }

      const syncedQuoteLibrary = await fetchSynced("quote-library", null);
      if (syncedQuoteLibrary) { setQuoteLibrary(syncedQuoteLibrary); saveLocal("quote-library", syncedQuoteLibrary); }

      const syncedQuoteFeedback = await fetchSynced("quote-feedback", null);
      if (syncedQuoteFeedback) { setQuoteFeedback(syncedQuoteFeedback); saveLocal("quote-feedback", syncedQuoteFeedback); }

      // Fetched directly (not via fetchSynced) because a server-side "lists"
      // key of null (never set yet) needs to be treated as a real empty
      // array here — that's precisely the case the one-time migration below
      // needs to run in — whereas a genuine network failure should leave
      // local cached state untouched, same as every other key above.
      let listsReachable = false, listsFromServer = null;
      try {
        const res = await fetch(`${SYNC_BASE}/lists`);
        if (res.ok) { listsFromServer = await res.json(); listsReachable = true; }
      } catch {}

      if (listsReachable) {
        let finalLists = listsFromServer || [];

        // One-time migration: Quick Capture used to be its own synced array
        // under the "quick-capture" key. Fold any existing items into the
        // new permanent Quick Capture list rather than losing them, then
        // persist immediately so this only ever runs once.
        if (!finalLists.some(l => l.id === QUICK_CAPTURE_LIST_ID)) {
          const legacyCaptured = await fetchSynced("quick-capture", null);
          if (legacyCaptured && legacyCaptured.length) {
            const migratedItems = legacyCaptured.map(c => ({ id: c.id, text: c.text, done: false, groupId: null, ts: c.ts }));
            finalLists = [...finalLists, { id: QUICK_CAPTURE_LIST_ID, title: "Quick Capture", lens: "all", protected: true, noGroups: true, groups: [], items: migratedItems }];
            pushSynced("lists", finalLists);
          }
        }
        setLists(finalLists);
        saveLocal("lists", finalLists);
      }

      // Gates the AI Briefing's auto-run — waiting for this means it never
      // fires against stale default/local data before the real synced
      // tasks/goals have had a chance to land.
      setSyncReady(true);
    })();
  }, []);

  // Called by KanbanBoard whenever tasks change
  const handleTasksChange = useCallback((updated) => {
    setTasks(updated);
    saveLocal("kanban-Tasks", updated);
    pushSynced("tasks", updated);
  }, []);

  // Called by GoalsTracker whenever goals change
  const handleGoalsChange = useCallback((updated) => {
    setGoals(updated);
    saveLocal("goals", updated);
    pushSynced("goals", updated);
  }, []);

  // Called by the Goals Coach once its proposed plan is applied. Creates a
  // new goal (or updates an existing one being broken down) plus its phases,
  // and appends the near-term tasks tagged with goalId/phaseId so progress
  // can later be derived from them. Reuses handleGoalsChange/handleTasksChange
  // — no separate sync path needed, goals and tasks already sync on their own.
  const handleApplyGoalPlan = useCallback((plan, targetGoalId) => {
    const now = Date.now();
    const phaseIds = (plan.phases || []).map((_, i) => `phase-${now}-${i}`);
    const newPhases = (plan.phases || []).map((p, i) => ({ id: phaseIds[i], title: p.title, dueDate: p.dueDate || null, complete: false }));

    let goalId = targetGoalId;
    let updatedGoals;
    if (targetGoalId) {
      updatedGoals = goals.map((g) => g.id === targetGoalId
        ? {
            ...g,
            title: plan.title || g.title,
            progressMode: plan.progressMode || g.progressMode || "manual",
            target: plan.target ?? g.target,
            unit: plan.unit ?? g.unit,
            dueDate: plan.dueDate || g.dueDate,
            lens: plan.lens || g.lens,
            phases: newPhases.length ? newPhases : (g.phases || []),
          }
        : g);
    } else {
      goalId = now;
      const newGoal = {
        id: goalId,
        title: plan.title,
        target: plan.target ?? null,
        progress: 0,
        unit: plan.unit ?? null,
        dueDate: plan.dueDate || null,
        lens: plan.lens || "work",
        complete: false,
        progressMode: plan.progressMode || "manual",
        phases: newPhases,
      };
      updatedGoals = [...goals, newGoal];
    }

    const newTasks = (plan.tasks || []).map((t, i) => {
      const id = now + i + 1;
      const recurrence = coachRecurrence(t.recurrence);
      return {
        id,
        title: t.title,
        col: "Backlog",
        lens: plan.lens || "work",
        priority: "Normal",
        dueDate: t.dueDate || (recurrence ? todayKey() : null),
        recurrence,
        seriesId: recurrence ? id : null,
        goalId,
        phaseId: (t.phaseIndex != null && phaseIds[t.phaseIndex]) ? phaseIds[t.phaseIndex] : (phaseIds[0] || null),
      };
    });
    const updatedTasks = [...tasks, ...newTasks];

    handleGoalsChange(updatedGoals);
    handleTasksChange(updatedTasks);
  }, [goals, tasks, handleGoalsChange, handleTasksChange]);

  // Deleting a goal never silently orphans its linked tasks or silently
  // deletes them either — the person explicitly chooses. cascade=true wipes
  // the linked tasks too; cascade=false just clears their goalId/phaseId so
  // they stay on the board as ordinary tasks.
  const handleDeleteGoal = useCallback((goalId, cascade) => {
    handleGoalsChange(goals.filter(g => g.id !== goalId));
    if (cascade) {
      handleTasksChange(tasks.filter(t => t.goalId !== goalId));
    } else {
      handleTasksChange(tasks.map(t => t.goalId === goalId ? { ...t, goalId: null, phaseId: null } : t));
    }
  }, [goals, tasks, handleGoalsChange, handleTasksChange]);

  // Same choice, scoped to a single phase instead of the whole goal.
  const handleRemovePhase = useCallback((goalId, phaseId, alsoRemoveTasks) => {
    handleGoalsChange(goals.map(g => g.id === goalId ? { ...g, phases: (g.phases || []).filter(p => p.id !== phaseId) } : g));
    if (alsoRemoveTasks) {
      handleTasksChange(tasks.filter(t => t.phaseId !== phaseId));
    } else {
      handleTasksChange(tasks.map(t => t.phaseId === phaseId ? { ...t, phaseId: null } : t));
    }
  }, [goals, tasks, handleGoalsChange, handleTasksChange]);

  // Called by the Coach's edit mode — small, scoped additions/changes to an
  // EXISTING goal rather than the full one-shot plan handleApplyGoalPlan
  // builds for a new goal. Kept as targeted deltas rather than "regenerate
  // everything" specifically because asking a model to reliably reproduce
  // untouched data alongside a small change is more failure-prone than just
  // describing the change itself.
  const handleApplyGoalDelta = useCallback((goalId, kind, payload) => {
    const now = Date.now();
    const goal = goals.find(g => g.id === goalId);
    if (kind === "add-phase") {
      const phaseId = `phase-${now}`;
      const newPhase = { id: phaseId, title: payload.title, dueDate: payload.dueDate || null, complete: false };
      handleGoalsChange(goals.map(g => g.id === goalId ? { ...g, phases: [...(g.phases || []), newPhase] } : g));
      const newTasks = (payload.tasks || []).map((t, i) => {
        const id = now + i + 1;
        const recurrence = coachRecurrence(t.recurrence);
        return { id, title: t.title, col: "Backlog", lens: goal?.lens || "work",
          priority: "Normal", dueDate: t.dueDate || (recurrence ? todayKey() : null), recurrence, seriesId: recurrence ? id : null, goalId, phaseId };
      });
      if (newTasks.length) handleTasksChange([...tasks, ...newTasks]);
    } else if (kind === "add-tasks") {
      const newTasks = (payload.tasks || []).map((t, i) => {
        const id = now + i + 1;
        const recurrence = coachRecurrence(t.recurrence);
        return { id, title: t.title, col: "Backlog", lens: goal?.lens || "work",
          priority: "Normal", dueDate: t.dueDate || (recurrence ? todayKey() : null), recurrence, seriesId: recurrence ? id : null, goalId, phaseId: payload.phaseId || null };
      });
      handleTasksChange([...tasks, ...newTasks]);
    } else if (kind === "split-task") {
      // Replaces one existing task with several smaller ones, inheriting
      // that task's goalId/phaseId/lens so they stay attached to the same
      // place in the plan.
      const original = tasks.find(t => t.id === payload.originalTaskId);
      if (!original) return;
      const subtasks = (payload.subtasks || []).map((t, i) => {
        const id = now + i + 1;
        const recurrence = coachRecurrence(t.recurrence);
        return { id, title: t.title, col: "Backlog", lens: original.lens,
          priority: original.priority || "Normal", dueDate: t.dueDate || original.dueDate || (recurrence ? todayKey() : null),
          recurrence, seriesId: recurrence ? id : null, goalId: original.goalId, phaseId: original.phaseId };
      });
      handleTasksChange([...tasks.filter(t => t.id !== payload.originalTaskId), ...subtasks]);
    }
  }, [goals, tasks, handleGoalsChange, handleTasksChange]);

  // Direct, deterministic edit — moving a phase's date is pure date
  // arithmetic, not a judgment call, so it doesn't go through the Coach.
  // Every task currently linked to the phase shifts by the same number of
  // days as the phase itself, so relative spacing between tasks survives
  // a slip rather than everything collapsing onto the new date.
  const handleReschedulePhase = useCallback((goalId, phaseId, newDueDate) => {
    const goal = goals.find(g => g.id === goalId);
    const phase = goal?.phases?.find(p => p.id === phaseId);
    if (!phase) return;
    const deltaDays = phase.dueDate
      ? Math.round((new Date(newDueDate) - new Date(phase.dueDate)) / 86400000)
      : 0;
    handleGoalsChange(goals.map(g => g.id === goalId
      ? { ...g, phases: g.phases.map(p => p.id === phaseId ? { ...p, dueDate: newDueDate } : p) }
      : g));
    if (deltaDays !== 0) {
      handleTasksChange(tasks.map(t => {
        if (t.phaseId !== phaseId || !t.dueDate) return t;
        const shifted = new Date(t.dueDate);
        shifted.setDate(shifted.getDate() + deltaDays);
        return { ...t, dueDate: shifted.toISOString().slice(0, 10) };
      }));
    }
  }, [goals, tasks, handleGoalsChange, handleTasksChange]);

  // Also direct/deterministic — pure position swap, no judgment involved.
  const handleReorderPhase = useCallback((goalId, phaseId, direction) => {
    handleGoalsChange(goals.map(g => {
      if (g.id !== goalId) return g;
      const phases = [...(g.phases || [])];
      const i = phases.findIndex(p => p.id === phaseId);
      const j = direction === "up" ? i - 1 : i + 1;
      if (i < 0 || j < 0 || j >= phases.length) return g;
      [phases[i], phases[j]] = [phases[j], phases[i]];
      return { ...g, phases };
    }));
  }, [goals, handleGoalsChange]);

  // Manually marking a phase complete cascades to mark every task linked to
  // it as Done — deliberately NOT routed through the Kanban recurrence path,
  // so bulk-completing a phase full of recurring tasks doesn't fire off a
  // flood of next-iteration spawns; it's a direct status change, not a
  // per-task "I did this today" signal.
  const handleCompletePhase = useCallback((goalId, phaseId) => {
    handleGoalsChange(goals.map(g => g.id === goalId
      ? { ...g, phases: g.phases.map(p => p.id === phaseId ? { ...p, complete: true } : p) }
      : g));
    handleTasksChange(tasks.map(t => t.phaseId === phaseId ? { ...t, col: "Done" } : t));
  }, [goals, tasks, handleGoalsChange, handleTasksChange]);

  // Called by ListsView whenever a list/group/item is added, changed, or removed
  const handleListsChange = useCallback((updated) => {
    setLists(updated);
    saveLocal("lists", updated);
    pushSynced("lists", updated);
  }, []);

  // Called by IntentionView/IntentionWidget for each of the three synced
  // Daily Intention keys — identical three-step pattern as everything above.
  const handleIntentionsChange = useCallback((updated) => {
    setIntentions(updated);
    saveLocal("intentions", updated);
    pushSynced("intentions", updated);
  }, []);
  const handleQuoteLibraryChange = useCallback((updated) => {
    setQuoteLibrary(updated);
    saveLocal("quote-library", updated);
    pushSynced("quote-library", updated);
  }, []);
  const handleQuoteFeedbackChange = useCallback((updated) => {
    setQuoteFeedback(updated);
    saveLocal("quote-feedback", updated);
    pushSynced("quote-feedback", updated);
  }, []);

  async function handleRefresh() {
    signIn(async () => {
      setCalLoading(true);
      const ev = await fetchCalendarEvents();
      setEvents(ev);
      saveLocal("calendar-events", ev);
      setCalLoading(false);
    });
  }

  function handleClear() {
    signOut();
    setEvents([]);
    localStorage.removeItem("calendar-events");
    localStorage.removeItem("gapi-token");
  }

  const VIEWS = [
    { id: "dashboard",   label: "Dashboard",    shortLabel: "Home",   icon: "⌂" },
    { id: "intention",   label: "Intention",    shortLabel: "Intent", icon: "☀" },
    { id: "calendar",    label: "Calendar",     shortLabel: "Cal",    icon: "▤" },
    { id: "tasks",       label: "Tasks",        shortLabel: "Tasks",  icon: "☑" },
    { id: "goals",       label: "Goals",        shortLabel: "Goals",  icon: "◎" },
    { id: "lists",       label: "Lists",        shortLabel: "Lists",  icon: "📋" },
    { id: "dinner",      label: "Dinner",       shortLabel: "Dinner", icon: "🍴" },
    { id: "news",        label: "Intelligence", shortLabel: "Intel",  icon: "◈" },
  ];

  const sharedProps = { lens, events, tasks, goals };

  function renderView() {
    switch (activeView) {
      case "intention":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <GreetingStrip {...sharedProps} />
            <IntentionView
              intentions={intentions}
              quoteLibrary={quoteLibrary}
              quoteFeedback={quoteFeedback}
              lens={lens}
              onIntentionsChange={handleIntentionsChange}
              onQuoteLibraryChange={handleQuoteLibraryChange}
              onQuoteFeedbackChange={handleQuoteFeedbackChange}
            />
          </div>
        );
      case "calendar":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <GreetingStrip {...sharedProps} />
            <CalendarView events={events} tasks={tasks} lens={lens} />
          </div>
        );
      case "tasks":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <GreetingStrip {...sharedProps} />
            <KanbanBoard
              tasks={tasks}
              onTasksChange={handleTasksChange}
              lens={lens}
              defaultTasks={DEFAULT_TASKS}
            />
          </div>
        );
      case "goals":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <GreetingStrip {...sharedProps} />
            <GoalsTracker
              lens={lens}
              goals={goals}
              tasks={tasks}
              onGoalsChange={handleGoalsChange}
              onApplyGoalPlan={handleApplyGoalPlan}
              onDeleteGoal={handleDeleteGoal}
              onRemovePhase={handleRemovePhase}
              onApplyGoalDelta={handleApplyGoalDelta}
              onReschedulePhase={handleReschedulePhase}
              onReorderPhase={handleReorderPhase}
              onCompletePhase={handleCompletePhase}
            />
          </div>
        );
      case "lists":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <GreetingStrip {...sharedProps} />
            <ListsView lens={lens} lists={lists} onListsChange={handleListsChange} />
          </div>
        );
      case "dinner":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <GreetingStrip {...sharedProps} />
            <DinnerView lens={lens} />
          </div>
        );
      case "news":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <GreetingStrip {...sharedProps} />
            <NewsFeed lens={lens} />
          </div>
        );
      default:
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <GreetingStrip {...sharedProps} />
            <IntentionWidget intentions={intentions} lens={lens} onOpenFull={() => setActiveView("intention")} />
            <KpiStrip {...sharedProps} />
            <AiBriefing {...sharedProps} autoRun={syncReady} />
            {isMobile ? (
              <>
                <CalendarView events={events} tasks={tasks} lens={lens} />
                <KanbanBoard
                  tasks={tasks}
                  onTasksChange={handleTasksChange}
                  lens={lens}
                  defaultTasks={DEFAULT_TASKS}
                />
                <GoalsTracker
                  lens={lens}
                  goals={goals}
                  tasks={tasks}
                  onGoalsChange={handleGoalsChange}
                  onApplyGoalPlan={handleApplyGoalPlan}
                  onDeleteGoal={handleDeleteGoal}
                  onRemovePhase={handleRemovePhase}
                  onApplyGoalDelta={handleApplyGoalDelta}
                  onReschedulePhase={handleReschedulePhase}
                  onReorderPhase={handleReorderPhase}
                  onCompletePhase={handleCompletePhase}
                />
              </>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 14 }}>
                  <CalendarView events={events} tasks={tasks} lens={lens} />
                  <GoalsTracker
                    lens={lens}
                    goals={goals}
                    tasks={tasks}
                    onGoalsChange={handleGoalsChange}
                    onApplyGoalPlan={handleApplyGoalPlan}
                    onDeleteGoal={handleDeleteGoal}
                    onRemovePhase={handleRemovePhase}
                    onApplyGoalDelta={handleApplyGoalDelta}
                    onReschedulePhase={handleReschedulePhase}
                    onReorderPhase={handleReorderPhase}
                    onCompletePhase={handleCompletePhase}
                  />
                </div>
                <KanbanBoard
                  tasks={tasks}
                  onTasksChange={handleTasksChange}
                  lens={lens}
                  defaultTasks={DEFAULT_TASKS}
                />
              </>
            )}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.1fr 1fr 1fr", gap: 14 }}>
              <NewsFeed lens={lens} />
              <DinnerWidget lens={lens} />
              <QuickCapture lists={lists} onListsChange={handleListsChange} />
            </div>
          </div>
        );
    }
  }

  return (
    <>
      <style>{THEME_TOKENS}</style>
      <style>{FONT_INJECT}</style>
      <style>{`
        @keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; display: inline-block; }
        button { transition: opacity 0.15s; }
        input:focus { border-color: ${tint(accent, 53)} !important; }
        select { appearance: none; }
      `}</style>

      <div style={{ minHeight: "100vh", background: "var(--bg)", fontFamily: "'DM Sans', sans-serif" }}>

        {/* TOP BAR */}
        <div style={{ position: "sticky", top: 0, zIndex: 100, background: "var(--panel)", borderBottom: "1px solid var(--border)", padding: isMobile ? "0 12px" : "0 20px", height: 54, display: "flex", alignItems: "center", gap: isMobile ? 10 : 14 }}>
          {!isMobile && (
            <button onClick={() => setSidebarOpen(o => !o)} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--ink-soft)", fontSize: 18, padding: 4, lineHeight: 1 }}>☰</button>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: `linear-gradient(135deg, ${accent}, ${tint(accent, 53)})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}>✦</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)", lineHeight: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{isMobile ? "Prince Fam" : "Prince Fam Command Center"}</div>
              {!isMobile && (
                <div style={{ fontSize: 10, color: "var(--ink-soft)", marginTop: 2, fontFamily: "'DM Mono', monospace" }}>
                  {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
                </div>
              )}
            </div>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: isMobile ? 6 : 10 }}>
            <ThemeToggle theme={theme} onChange={setTheme} />
            {calendarReady && (
              <button onClick={handleRefresh} disabled={calLoading} title="Refresh Calendar" style={{ padding: isMobile ? "8px 10px" : "5px 14px", background: calLoading ? "var(--border-soft)" : accent, border: "none", borderRadius: 7, cursor: calLoading ? "default" : "pointer", color: calLoading ? "var(--ink-faint)" : "var(--bg)", fontSize: 11, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
                {isMobile ? "↺" : (calLoading ? "Refreshing…" : "↺ Refresh Calendar")}
              </button>
            )}
            {events.length > 0 && !isMobile && (
              <button onClick={handleClear} style={{ padding: "5px 12px", background: "transparent", border: "1px solid var(--border-soft)", borderRadius: 7, cursor: "pointer", color: "var(--ink-soft)", fontSize: 11, fontFamily: "'DM Sans', sans-serif" }}>Clear</button>
            )}
            <div style={{ width: 30, height: 30, borderRadius: "50%", background: "var(--panel-alt)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: accent }}>SP</span>
            </div>
          </div>
        </div>

        {/* LENS STRIP — already horizontally scrollable, works fine as-is on mobile */}
        <div style={{ background: "var(--panel)", borderBottom: "1px solid var(--border)", padding: "0 20px", display: "flex", gap: 0, overflowX: "auto" }}>
          {LENSES.map(l => <LensTab key={l.id} lens={l} active={lens === l.id} onClick={() => setLens(l.id)} />)}
        </div>

        {/* BODY */}
        <div style={{ display: "flex", maxWidth: 1600, margin: "0 auto" }}>

          {/* Sidebar — desktop only; mobile uses the fixed bottom bar below instead.
              Sticky + alignSelf:flex-start keeps it pinned just under the top bar as
              the main column scrolls, instead of scrolling away with the page. */}
          {!isMobile && sidebarOpen && (
            <div style={{ width: 200, flexShrink: 0, alignSelf: "flex-start", position: "sticky", top: 54, maxHeight: "calc(100vh - 54px)", overflowY: "auto", borderRight: "1px solid var(--border)", padding: "20px 12px", display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--ink-faint)", marginBottom: 6, paddingLeft: 8, fontFamily: "'DM Mono', monospace" }}>Views</div>
              {VIEWS.map(v => (
                <button key={v.id} onClick={() => setActiveView(v.id)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 7, border: "none", background: activeView === v.id ? tint(accent, 13) : "transparent", color: activeView === v.id ? accent : "var(--ink-soft)", cursor: "pointer", fontSize: 13, fontFamily: "'DM Sans', sans-serif", textAlign: "left", fontWeight: activeView === v.id ? 600 : 400 }}>
                  {v.label}
                </button>
              ))}
              <div style={{ borderTop: "1px solid var(--border)", marginTop: 12, paddingTop: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--ink-faint)", marginBottom: 6, paddingLeft: 8, fontFamily: "'DM Mono', monospace" }}>Lenses</div>
                {LENSES.filter(l => l.id !== "all").map(l => (
                  <button key={l.id} onClick={() => setLens(l.id)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 7, border: "none", background: lens === l.id ? tint(l.accent, 8) : "transparent", color: lens === l.id ? l.accentText : "var(--ink-soft)", cursor: "pointer", fontSize: 13, width: "100%", fontFamily: "'DM Sans', sans-serif", textAlign: "left" }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: l.accent, flexShrink: 0 }} />
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Main */}
          <div style={{ flex: 1, padding: isMobile ? "14px 12px 90px" : "20px 20px 40px", overflow: "auto", minWidth: 0 }}>
            {renderView()}
          </div>
        </div>

        {/* Footer — hidden on mobile, no room and nothing essential in it */}
        {!isMobile && (
          <div style={{ background: "var(--panel)", borderTop: "1px solid var(--border)", padding: "10px 20px", display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--ink-faint)", fontFamily: "'DM Mono', monospace" }}>
            <span>PRINCE FAM COMMAND CENTER · v3.2</span>
            <span style={{ color: "var(--green)" }}>● LIVE</span>
          </div>
        )}
      </div>

      {/* MOBILE BOTTOM NAV — replaces the sidebar entirely on a narrow screen */}
      {isMobile && (
        <nav style={{ position: "fixed", bottom: 0, left: 0, right: 0, display: "flex", background: "var(--panel)", borderTop: "1px solid var(--border)", paddingBottom: "env(safe-area-inset-bottom, 0px)", zIndex: 100 }}>
          {VIEWS.map(v => {
            const active = activeView === v.id;
            return (
              <button key={v.id} onClick={() => setActiveView(v.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "8px 2px", background: "transparent", border: "none", color: active ? accent : "var(--ink-dim)" }}>
                <span style={{ fontSize: 17, lineHeight: 1 }}>{v.icon}</span>
                <span style={{ fontSize: 9, fontFamily: "'DM Sans', sans-serif", fontWeight: active ? 600 : 400 }}>{v.shortLabel}</span>
              </button>
            );
          })}
        </nav>
      )}
    </>
  );
}